/* eslint-disable */
import React, { Fragment, useState, useContext, useRef } from 'react'
import { useTheme } from '@emotion/react'
import PropTypes from 'prop-types'
import { pick } from 'ramda'
import { StyleSheet, css } from 'aphrodite/no-important'
// Grid components
import { GridRow } from './grid-row'
import { GridHeaderCell } from './grid-header-cell'
import { ThemeContext } from '../theme'
import { GridPager } from './grid-pager'
import { Loader } from '../loader'
import { GRID_DATA_LOADER_ZINDEX } from '../utils'

const propTypes = {
  loader: PropTypes.bool,
  icons: PropTypes.object,
  rowData: PropTypes.array,
  headerDefs: PropTypes.arrayOf(
    PropTypes.shape({
      visible: PropTypes.bool,
      pin: PropTypes.oneOf(['right', 'left', 'none']),
      sortable: PropTypes.bool,
      filter: PropTypes.bool,
      dataType: PropTypes.oneOf(['string', 'number', 'date']),
      text: PropTypes.string,
      field: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.string),
      icon: PropTypes.string,
      colStyles: PropTypes.object, // styles for this column
      colCellsStyles: PropTypes.object, // styles for all cells of this column
      cellRenderer: PropTypes.func // function that returns JSX for cell content
    })
  ),
  mergeHeaderRows: PropTypes.bool,
  filters: PropTypes.object,
  gridWidth: PropTypes.number,
  gridHeight: PropTypes.number,
  gridHeaderHeight: PropTypes.number,
  gridFooterHeight: PropTypes.number,
  gridContainerHeight: PropTypes.string,
  rowHeight: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onColPinChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  onColFilterToggle: PropTypes.func,
  onChangeTextAlignment: PropTypes.func,
  onFilterTypeUpdate: PropTypes.func,
  onApplyFilter: PropTypes.func,
  onResetCol: PropTypes.func,
  onSort: PropTypes.func,
  onClickCell: PropTypes.func,
  onPopupMenu: PropTypes.func,
  onResize: PropTypes.func,
  onColConfig: PropTypes.func,
  onResetGrid: PropTypes.func,
  onLoadPage: PropTypes.func,
  onColVisibilityChange: PropTypes.func,
  gridContainerStyles: PropTypes.object,
  selectItems: PropTypes.object,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  showFooter: PropTypes.bool,
  defaultColStyles: PropTypes.object, // styles for all cols
  defaultCellStyle: PropTypes.object, // styles for all cells
  showEllipsisCol: PropTypes.bool,
  enableHover: PropTypes.bool,
  isMobileLayout: PropTypes.bool,
  showPager: PropTypes.bool,
  canSelectRows: PropTypes.bool,
  hideGridHeaderMenus: PropTypes.bool
}
const defaultProps = {
  gridContainerStyles: {},
  loader: false,
  rowHover: false,
  showFooter: true,
  showEllipsisCol: true,
  enableHover: true,
  showPager: false,
  canSelectRows: true,
  hideGridHeaderMenus: false
}
const PINNED_Z_INDEX = 1500

let prvScrollLeft = 0
let prvScrollTop = 0

const DataGrid = (props) => {
  const [activeRow, setActiveRow] = useState(-1)
  const [selectedRecordId, setSelectedRecordId] = useState('') // 619daf859673882d9bb587dd
  const [resizeStatus, setResizeStatus] = useState('none')
  const [screenSize, setScreenSize] = useState('normal')

  const theme = useTheme()
  // Context and Refs
  const context = useContext(ThemeContext)
  const grid = useRef()
  const scroller = useRef()
  const header = useRef()
  const footer = useRef()

  // Select user theme or default theme from context
  let dataGrid
  if (theme && theme.dataGrid) {
    dataGrid = theme.dataGrid
  } else {
    dataGrid = context.theme.dataGrid
  }

  // Data grid theme
  const {
    gridBackground,
    gridBorderColor,
    gridPinnedBorderColor,
    gridMaskColor,
    gridRowsContainerBackground,
    gridHeaderBackground,
    gridHeaderTextColor,
    gridHeaderRightBorderColor,
    gridHeaderBottomBorderColor,
    gridHeaderFontSize,
    gridHeaderFilterFontSize,
    gridHeaderHeight: gridThemeHeaderHeight, // already a prop value
    gridHeaderFilterMenuBackground,
    gridHeaderFilterMenuTextColor,
    gridHeaderFilterMenuBorderColor,
    // Rows
    gridRowsBackground,
    gridRowsAltBackground,
    gridRowsTextColor,
    gridRowsLinkTextColor,
    gridRowsRightBorderColor,
    gridRowsBottomBorderColor,
    gridRowsFontSize,
    gridHighlightTextBackground,
    gridHighlightTextColor,
    gridRowsHoverBackground,
    gridRowsHoverTextColor,
    gridRowSelectionBackground,
    gridRowSelectionOpacity,
    // Footer
    gridFooterBackground,
    gridFooterTextColor,
    gridFooterFontSize,
    gridFooterRightBorderColor,
    gridFooterTopBorderColor,
    gridFooterHeight: gridThemeFooterHeight,
    // Pager
    gridPagerBackground,
    gridPagerTopBorderColor,
    gridPagerTextColor,
    gridPagerFontSize,
    gridPagerDropdownTextColor,
    gridPagerIconsTextColor,
    gridPagerIconsBackground,
    gridPagerHeight
  } = dataGrid

  const {
    headerDefs,
    rowData,
    icons,
    gridWidth,
    gridHeight,
    gridHeaderHeight,
    gridFooterHeight,
    filters,
    onColPinChange,
    onFilterChange,
    onColFilterToggle,
    onFilterTypeUpdate,
    onChangeTextAlignment,
    onApplyFilter,
    onResetCol,
    onSort,
    showFooter,
    defaultCellStyle,
    rowHeight,
    onClickCell,
    rowsPerPage,
    enableHover,
    showEllipsisCol,
    loader,
    gridContainerHeight,
    isMobileLayout,
    showPager,
    onSelectItemChange,
    onSelectAllChange
  } = props

  let leftColsWidth = 0
  let leftColsMinWidth = 0
  let leftColsMaxWidth = 0
  let centeredColsWidth = 0
  let centeredColsMinWidth = 0
  let centeredColsMaxWidth = 0
  let rightColsWidth = 0
  let rightColsMinWidth = 0
  let rightColsMaxWidth = 0
  const leftPinnedHeaderDefs = []
  const centeredHeaderDefs = []
  const rightPinnedHeaderDefs = []
  const leftPinnedCols = ['id'] // Left pinned col names (assuming that data set has a column named id)
  const rightPinnedCols = ['id'] // Right pinned col names
  const centeredCols = ['id'] // Centered pinned col names

  // Splitting headerDefs into left, centered and right headerDefs and measuring widths of each section of grid
  headerDefs.forEach((el) => {
    if (el.visible) {
      if (!isMobileLayout) {
        switch (el.pin) {
          case 'left':
            leftPinnedHeaderDefs.push(el)
            leftPinnedCols.push(el.field)
            leftColsWidth += el.colStyles.width
            leftColsMinWidth += el.colStyles.minWidth
            leftColsMaxWidth += el.colStyles.maxWidth
            break
          case 'right':
            rightPinnedHeaderDefs.push(el)
            rightPinnedCols.push(el.field)
            rightColsWidth += el.colStyles.width
            rightColsMinWidth += el.colStyles.minWidth
            rightColsMaxWidth += el.colStyles.maxWidth
            break
          case 'none':
          default:
            centeredHeaderDefs.push(el)
            centeredColsWidth += el.colStyles.width
            centeredColsMinWidth += el.colStyles.minWidth
            centeredColsMaxWidth += el.colStyles.maxWidth
            centeredCols.push(el.field)
        }
      } else {
        switch (el.pin) {
          case 'left':
            leftPinnedHeaderDefs.push(el)
            leftPinnedCols.push(el.field)
            leftColsWidth += el.colStyles.width
            leftColsMinWidth += el.colStyles.minWidth
            leftColsMaxWidth += el.colStyles.maxWidth
            break
          // case 'right':
          //   rightPinnedHeaderDefs.push(el);
          //   rightPinnedCols.push(el.field);
          //   rightColsWidth += el.colStyles.width;
          //   rightColsMinWidth += el.colStyles.minWidth;
          //   rightColsMaxWidth += el.colStyles.maxWidth;
          //   break;
          case 'none':
          case 'right':
          default:
            centeredHeaderDefs.push(el)
            centeredColsWidth += el.colStyles.width
            centeredColsMinWidth += el.colStyles.minWidth
            centeredColsMaxWidth += el.colStyles.maxWidth
            centeredCols.push(el.field)
        }
      }
    }
  })

  if (showEllipsisCol && !isMobileLayout) {
    rightColsWidth += 35
    rightColsMinWidth += 35
    rightColsMaxWidth += 35
  }

  if (showEllipsisCol && isMobileLayout) {
    centeredColsWidth += 35
    centeredColsMinWidth += 35
    centeredColsMaxWidth += 35
  }

  // if (process.env.NODE_ENV === 'development') {
  //   console.log(`
  //     leftColsWidth = ${leftColsWidth}
  //     centeredColsWidth = ${centeredColsWidth}
  //     rightColsWidth = ${rightColsWidth}
  //   `);
  // }

  const gridRowsContainerHeight =
    gridHeight -
    gridHeaderHeight -
    (showFooter ? gridFooterHeight : 0) -
    (showPager ? gridPagerHeight : 0)
  // const gridRowsContainerHeight = gridHeight - gridHeaderHeight - (showFooter ? gridFooterHeight : 0) + 16;
  const centeredContainerWidth = gridWidth - leftColsWidth - rightColsWidth

  // ---------------------- Styles ----------------------
  const styles = StyleSheet.create({
    grid: {
      width: `${gridWidth}px`,
      height: `${gridHeight - gridPagerHeight}px`,
      // height: `${gridRowsContainerHeight}px`,
      border: `1px solid ${gridBorderColor}`,
      fontSize: '16px',
      position: 'relative',
      overflow: 'hidden'
    },
    dataLoader: {
      background: '#004080', // TODO: data loader spinner background color (theme)
      opacity: '0.75',
      position: 'absolute',
      zIndex: '5000',
      // zIndex: GRID_DATA_LOADER_ZINDEX,
      width: '100%',
      height: `${gridHeight}px`
    },
    gridHeaderContainer: {
      display: 'flex',
      height: `${gridHeaderHeight}px`,
      borderBottom: `1px solid ${gridHeaderBottomBorderColor}`,
      color: gridHeaderTextColor,
      fontSize: gridHeaderFontSize
    },
    gridHeaderLeftContainer: {
      display: 'flex',
      // width: `${leftColsWidth}px`,
      borderRight: `1px solid ${gridPinnedBorderColor}`,
      zIndex: PINNED_Z_INDEX
    },
    gridHeaderCenteredContainer: {
      display: 'flex',
      width: `${centeredContainerWidth}px`,
      zIndex: '2'
    },
    gridHeaderRightContainer: {
      display: 'flex',
      width: `${rightColsWidth}px`,
      // TODO: grid header left/right border color (theme)
      borderLeft: `1px solid ${gridPinnedBorderColor}`,
      zIndex: PINNED_Z_INDEX,
      position: 'relative',
      left: '-2px'
    },
    gridRowsContainer: {
      display: 'flex',
      height: `${gridRowsContainerHeight}px`,
      position: props.rowData.length === 0 ? 'relative' : '',
      overflow: 'hidden scroll',
      background: gridBackground,
      '-webkit-overflow-scrolling': 'touch'
    },
    gridRowsContainerLeft: {
      width: `${leftColsWidth}px`,
      // height: `${rowHeight * rowsPerPage}px`
      height: `${rowHeight * rowData.length}px`
      // TODO: Theme color variable (pinned areas border left/right)
      // borderRight: '1px solid black'
    },
    gridRowsContainerCentered: {
      width: `${centeredContainerWidth}px`,
      // height: `${rowHeight * rowsPerPage}px`,
      height: `${rowHeight * rowData.length}px`,
      overflowX: 'auto',
      overflowY: 'hidden'
    },
    gridRowsContainerRight: {
      width: `${rightColsWidth}px`,
      height: `${rowHeight * rowData.length}px`
      // height: `${rowHeight * rowsPerPage}px`
      // borderLeft: '1px solid black'
    },
    gridFooter: {
      display: 'flex',
      height: `${gridFooterHeight}px`,
      // TODO: Theme color variable (grid footer bottom border color)
      borderBottom: '1px solid black'
    },
    footerCells: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: PINNED_Z_INDEX,
      background: gridFooterBackground
    },
    ellipsisHeaderCell: {
      height: `${gridHeaderHeight}px`,
      width: '35px',
      minWidth: '35px',
      maxWidth: '35px',
      background: gridHeaderBackground,
      color: gridHeaderTextColor,
      borderBottom: `1px solid ${gridHeaderBottomBorderColor}`,
      fontSize: gridHeaderFontSize
    }
  })

  // ---------------------- Event handlers ----------------------
  const handleScroll = (e) => {
    const { target } = e
    let isHorizontalScroll = prvScrollLeft !== target.scrollLeft
    let isVerticalScroll = prvScrollTop !== target.scrollTop

    prvScrollTop = target.scrollTop
    prvScrollLeft = target.scrollLeft

    if (isHorizontalScroll && !isVerticalScroll) {
      // header.current.style.transform = 'translateX('.concat(-1 * target.scrollLeft, 'px)');

      header.current.style.transform = `translateX(${-1 * target.scrollLeft}px)`

      // header.current.style['-webkit-transform'] = `translate3d(${-1 * target.scrollLeft}px,0,0)`;
    }
  }

  const handleOnRowMoueOver = (idx) => {
    // console.log('Mouse leaving grid!');
    setActiveRow(idx)
  }

  const handleOnMouseLeave = () => {
    setActiveRow(-1)
  }

  const handleOnClickEllipsis = (e, idx) => {
    props.onPopupMenu(e, idx)
  }

  const handleRowHover = () => {}

  const handleOnClickCell = (col, value, recordId, e, rowIndex) => {
    if (recordId === selectedRecordId) {
      setSelectedRecordId('')
    } else {
      setSelectedRecordId(recordId)
    }
    onClickCell(col, value, recordId, e, rowIndex)
  }

  // ---------------------- Generating left pinned header and footer cells ----------------------
  const leftPinnedHeaderCells = []
  const leftPinnedFooterCells = []

  leftPinnedHeaderDefs.forEach((el) => {
    leftPinnedHeaderCells.push(
      <GridHeaderCell
        key={el.key}
        dataId={`${el.key}_${el.pin}_${el}`}
        icons={icons}
        gridWidth={gridWidth}
        headerDef={el}
        headerHeight={gridHeaderHeight}
        mergeHeaderRows={props.mergeHeaderRows}
        filterType={el.filterType}
        filterValue={filters[el.field] ? filters[el.field].value : ''}
        selectItems={
          el.filter && el.filterType === 'select'
            ? filters[el.field].items
            : null
        }
        onSelectItemChange={onSelectItemChange}
        onSelectAllChange={onSelectAllChange}
        onPinChange={onColPinChange}
        onFilterChange={onFilterChange}
        onFilterToggle={onColFilterToggle}
        onFilterTypeUpdate={onFilterTypeUpdate}
        onChangeTextAlignment={onChangeTextAlignment}
        onApplyFilter={onApplyFilter}
        onResetCol={onResetCol}
        onSort={onSort}
        hideGridHeaderMenus={props.hideGridHeaderMenus}
        theme={{
          gridHeaderBackground,
          gridHeaderTextColor,
          gridHeaderRightBorderColor,
          gridHeaderBottomBorderColor,
          gridHeaderFontSize,
          gridHeaderFilterFontSize,
          gridHeaderHeight,
          gridHeaderFilterMenuBackground,
          gridHeaderFilterMenuTextColor,
          gridHeaderFilterMenuBorderColor
        }}
      />
    )

    if (showFooter) {
      leftPinnedFooterCells.push(
        <div
          key={`${el.key}_footer`}
          className={css(styles.footerCells)}
          style={{
            width: `${el.colStyles.width}px`,
            minWidth: `${el.colStyles.minWidth}px`,
            maxWidth: `${el.colStyles.maxWidth}px`
          }}>
          {el.text}
        </div>
      )
    }
  })

  // ---------------------- Generating centered header and footer cells ----------------------
  const centeredHeaderCells = []
  const centeredFooterCells = []

  centeredHeaderDefs.forEach((el) => {
    centeredHeaderCells.push(
      <GridHeaderCell
        key={el.key}
        dataId={`${el.key}_${el.pin}_${el}`}
        icons={props.icons}
        gridWidth={gridWidth}
        headerDef={el}
        mergeHeaderRows={props.mergeHeaderRows}
        headerHeight={gridHeaderHeight}
        filterType={el.filterType}
        filterValue={filters[el.field] ? filters[el.field].value : ''}
        selectItems={
          el.filter && el.filterType === 'select'
            ? filters[el.field].items
            : null
        }
        onSelectItemChange={onSelectItemChange}
        onSelectAllChange={onSelectAllChange}
        onPinChange={onColPinChange}
        onFilterChange={onFilterChange}
        onFilterToggle={onColFilterToggle}
        onFilterTypeUpdate={onFilterTypeUpdate}
        onApplyFilter={onApplyFilter}
        onResetCol={onResetCol}
        onChangeTextAlignment={onChangeTextAlignment}
        onSort={onSort}
        hideGridHeaderMenus={props.hideGridHeaderMenus}
        theme={{
          gridHeaderBackground,
          gridHeaderTextColor,
          gridHeaderRightBorderColor,
          gridHeaderBottomBorderColor,
          gridHeaderFontSize,
          gridHeaderFilterFontSize,
          gridHeaderHeight,
          gridHeaderFilterMenuBackground,
          gridHeaderFilterMenuTextColor,
          gridHeaderFilterMenuBorderColor
        }}
      />
    )

    if (showFooter) {
      centeredFooterCells.push(
        <div
          key={`${el.key}_footer`}
          className={css(styles.footerCells)}
          style={{
            width: `${el.colStyles.width}px`,
            minWidth: `${el.colStyles.minWidth}px`,
            maxWidth: `${el.colStyles.maxWidth}px`
          }}>
          {el.text}
        </div>
      )
    }
  })

  // ---- Ellipsis header for centered header & footer cells ----
  if (showEllipsisCol && isMobileLayout) {
    centeredHeaderCells.push(
      <div
        key='header-ellipsis-col_centered'
        className={`fx fx__hv-center ${css(styles.ellipsisHeaderCell)}`}>
        <i className='fa fa-ellipsis-h' />
      </div>
    )
    // TODO: ellipsis footer cells
  }

  // ---------------------- Generating right pinned header and footer cells ----------------------
  const rightPinnedHeaderCells = []
  const rightPinnedFooterCells = []
  if (!isMobileLayout) {
    rightPinnedHeaderDefs.forEach((el) => {
      rightPinnedHeaderCells.push(
        <GridHeaderCell
          key={el.key}
          dataId={`${el.key}_${el.pin}_${el}`}
          icons={props.icons}
          gridWidth={gridWidth}
          headerDef={el}
          mergeHeaderRows={props.mergeHeaderRows}
          headerHeight={gridHeaderHeight}
          filterType={el.filterType}
          filterValue={filters[el.field] ? filters[el.field].value : ''}
          selectItems={
            el.filter && el.filterType === 'select'
              ? filters[el.field].items
              : null
          }
          onSelectItemChange={onSelectItemChange}
          onSelectAllChange={onSelectAllChange}
          onPinChange={onColPinChange}
          onFilterChange={onFilterChange}
          onFilterToggle={onColFilterToggle}
          onFilterTypeUpdate={onFilterTypeUpdate}
          onChangeTextAlignment={onChangeTextAlignment}
          onApplyFilter={onApplyFilter}
          onResetCol={onResetCol}
          onSort={onSort}
          hideGridHeaderMenus={props.hideGridHeaderMenus}
          theme={{
            gridHeaderBackground,
            gridHeaderTextColor,
            gridHeaderRightBorderColor,
            gridHeaderBottomBorderColor,
            gridHeaderFontSize,
            gridHeaderFilterFontSize,
            gridHeaderHeight,
            gridHeaderFilterMenuBackground,
            gridHeaderFilterMenuTextColor,
            gridHeaderFilterMenuBorderColor
          }}
        />
      )
    })

    if (showFooter) {
      rightPinnedFooterCells.push(
        <div
          key={`${el.key}_footer`}
          className={css(styles.footerCells)}
          style={{
            width: `${el.colStyles.width}px`,
            minWidth: `${el.colStyles.minWidth}px`,
            maxWidth: `${el.colStyles.maxWidth}px`
          }}>
          {el.text}
        </div>
      )
    }
  }

  // ---- Ellipsis header for right pinned header cells ----
  if (showEllipsisCol && !isMobileLayout) {
    rightPinnedHeaderCells.push(
      <div
        key='header-ellipsis-col_right'
        className={`fx fx__hv-center ${css(styles.ellipsisHeaderCell)}`}>
        <i className='fa fa-ellipsis-h' />
      </div>
    )
    // TODO: ellipsis footer cells
  }

  // ---------------------- Splitting row data into left, centered and right pinned cols ----------------------
  const leftPinnedRowData = rowData.map((el) => pick(leftPinnedCols, el))
  const rightPinnedRowData = rowData.map((el) => pick(rightPinnedCols, el))
  const centeredRowData = rowData.map((el) => pick(centeredCols, el))

  // ---------------------- Generating left pinned data rows ----------------------
  const leftPinnedRows = []
  leftPinnedRowData.forEach((el, idx) => {
    leftPinnedRows.push(
      <GridRow
        data={el}
        key={`${el.id}_left`}
        dataId={`${el.id}_${idx}_left`}
        recordId={el.id}
        isSelected={props.canSelectRows && el.id === selectedRecordId}
        index={idx}
        isHover={idx === activeRow}
        headerDefs={leftPinnedHeaderDefs}
        filters={filters}
        defaultCellStyle={defaultCellStyle}
        rowBg={gridRowsBackground}
        rowAlternateBg={gridRowsAltBackground}
        rowHoverBg={gridRowsHoverBackground}
        defaultRowStyle={{
          height: `${rowHeight}px`,
          borderRight:
            leftPinnedHeaderDefs.length > 0
              ? `1px solid ${gridPinnedBorderColor}`
              : 'none'
        }}
        onRowMouseOver={handleOnRowMoueOver}
        onClickCell={handleOnClickCell}
        enableHover={enableHover}
        theme={{
          gridRowsTextColor,
          gridRowsLinkTextColor,
          gridRowsRightBorderColor,
          gridRowsBottomBorderColor,
          gridRowsFontSize,
          gridHighlightTextBackground,
          gridHighlightTextColor
        }}
      />
    )
  })

  // ---------------------- Generating centered pinned data rows ----------------------
  const centeredRows = []
  centeredRowData.forEach((el, idx) => {
    centeredRows.push(
      <GridRow
        data={el}
        key={`${el.id}_center`}
        dataId={`${el.id}_${idx}_center`}
        recordId={el.id}
        isSelected={props.canSelectRows && el.id === selectedRecordId}
        index={idx}
        isHover={idx === activeRow}
        filters={filters}
        onRowHover={handleRowHover}
        headerDefs={centeredHeaderDefs}
        defaultCellStyle={defaultCellStyle}
        defaultRowStyle={{
          height: `${rowHeight}px`
        }}
        onClickCell={handleOnClickCell}
        rowBg={gridRowsBackground}
        rowAlternateBg={gridRowsAltBackground}
        rowHoverBg={gridRowsHoverBackground}
        onRowMouseOver={handleOnRowMoueOver}
        enableHover={enableHover}
        gridPosition='center'
        onClickEllipsis={handleOnClickEllipsis}
        addEllipsisCol={isMobileLayout && showEllipsisCol}
        theme={{
          gridRowsTextColor,
          gridRowsLinkTextColor,
          gridRowsRightBorderColor,
          gridRowsBottomBorderColor,
          gridRowsFontSize,
          gridHighlightTextBackground,
          gridHighlightTextColor
        }}
      />
    )
  })

  // ---------------------- Generating right pinned data rows ----------------------
  const rightPinnedRows = []
  if (!isMobileLayout) {
    rightPinnedRowData.forEach((el, idx) => {
      rightPinnedRows.push(
        <GridRow
          data={el}
          key={`${el.id}_right`}
          dataId={`${el.id}_${idx}right`}
          recordId={el.id}
          isSelected={props.canSelectRows && el.id === selectedRecordId}
          index={idx}
          isHover={idx === activeRow}
          filters={filters}
          onRowHover={handleRowHover}
          headerDefs={rightPinnedHeaderDefs}
          defaultCellStyle={defaultCellStyle}
          rowBg={gridRowsBackground}
          rowAlternateBg={gridRowsAltBackground}
          rowHoverBg={gridRowsHoverBackground}
          onRowMouseOver={handleOnRowMoueOver}
          defaultRowStyle={{
            borderLeft: `1px solid ${gridPinnedBorderColor}`,
            height: `${rowHeight}px`
          }}
          enableHover={enableHover}
          onClickEllipsis={handleOnClickEllipsis}
          addEllipsisCol={!isMobileLayout && showEllipsisCol}
          theme={{
            gridRowsTextColor,
            gridRowsLinkTextColor,
            gridRowsRightBorderColor,
            gridRowsBottomBorderColor,
            gridRowsFontSize,
            gridHighlightTextBackground,
            gridHighlightTextColor
          }}
        />
      )
    })
  }

  // ---------------------- Data Grid Component ----------------------
  return (
    <>
      <div ref={grid} className={css(styles.grid)}>
        {/* Data loader */}
        {/* {loader
          ? <div className={css(styles.dataLoader)}>
              <Loader iconType="cog" iconSize="5x" text="Loading data" />
            </div>
          : null
        } */}
        {/* ---------------- Grid Header ---------------- */}
        <div className={css(styles.gridHeaderContainer)}>
          {/* Left pinned header */}
          <div className={css(styles.gridHeaderLeftContainer)}>
            {leftPinnedHeaderCells}
          </div>
          {/* Centered headers */}
          <div className={css(styles.gridHeaderCenteredContainer)} ref={header}>
            {centeredHeaderCells}
          </div>
          {/* Right pinned header */}
          {!isMobileLayout && showEllipsisCol ? (
            <div className={css(styles.gridHeaderRightContainer)}>
              {rightPinnedHeaderCells}
            </div>
          ) : null}
        </div>
        {/* ---------------- Grid Rows ---------------- */}
        <div
          ref={scroller}
          className={css(styles.gridRowsContainer)}
          onScroll={handleScroll}>
          {/* Zero row message */}
          {!loader && props.rowData.length === 0 && (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                fontWeight: '700'
              }}>
              {props.zeroRowMessage}
            </div>
          )}

          {/* Data loader */}
          {loader ? (
            <div className={css(styles.dataLoader)}>
              <Loader iconType='cog' iconSize='5x' text='Loading data' />
            </div>
          ) : null}
          {/* Left pinned rows */}
          <div
            className={css(styles.gridRowsContainerLeft)}
            onMouseLeave={handleOnMouseLeave}>
            {leftPinnedRows}
          </div>
          {/* Centered rows */}
          <div
            className={css(styles.gridRowsContainerCentered)}
            onMouseLeave={handleOnMouseLeave}>
            {centeredRows}
          </div>
          {/* Right pinned rows */}
          {!isMobileLayout ? (
            <div
              className={css(styles.gridRowsContainerRight)}
              onMouseLeave={handleOnMouseLeave}>
              {rightPinnedRows}
            </div>
          ) : null}
        </div>
        {/* Grid footer */}
        {showFooter ? <div className={css(styles.gridFooter)}></div> : null}
      </div>
      {/* Grid pager */}
      <GridPager
        currentPage={props.currentPage}
        totalPages={props.totalPages}
        loadPage={props.onLoadPage}
        theme={{
          gridPagerBackground,
          gridPagerTopBorderColor,
          gridPagerTextColor,
          gridPagerFontSize,
          gridPagerDropdownTextColor,
          gridPagerIconsTextColor,
          gridPagerIconsBackground,
          gridPagerHeight
        }}
      />
    </>
  )
}

DataGrid.defaultProps = {
  zeroRowMessage: 'No data found!',
  mergeHeaderRows: false
}

export { DataGrid }
